'use es6';

import { isPositionAboveBounds, isPositionBelowBounds, isPositionToTheLeftOfBounds, isPositionToTheRightOfBounds, isPositionWithinBounds, isPositionWithinHorizontalBounds } from '../geometry/basic';
import { closeToEdgeDistancePerTriangle, determineLeftOrRightHalfPositionWithinBounds, determinePositionTriangleWithinBounds, determineTopOrBottomHalfPositionWithinBounds, horizontalAndVericalCloseToEdgeDistanceForThisNode, isCloseToLeftOrRightEdge, isCloseToTopOrBottomEdge, isTrianglePositionCloseToEdgeOfBounds } from '../geometry/triangle';
import { areColumnsInRow, areRowsInColumn, calculateOutsideLayoutTreeBufferAreaHoverPositions, getChildrenForCurrentNode, getClosestDndArea, getDeepestHoveredNode, getFirstChildOfNode, getLastChildOfNode, getLayoutTreeChildrenMaps, isBetweenTwoColumns, isBetweenTwoRows, isFirstTopLevelSectionRow, isLastTopLevelSectionRow, isTopLevelSectionRow } from './layoutTreeHoverPositionUtils';
import { bufferedCloseToEdgeDistancesWithNestedRows, bufferedCloseToEdgeDistancesWithoutNestedRows, hoverTypes, VERTICAL_CHILD_COLUMN_BUFFER_PERCENTAGE } from '../constants/dndAreaHoverPositionConstants';

//TODO remove references to layoutSection in favor of layoutTree

const buildHoverPositionForRow = (node, otherProps) => {
  return Object.assign({
    depth: node.depth,
    hasLayoutStyles: node.hasLayoutStyles,
    id: node.id,
    isRow: true,
    measuredHeight: node.height,
    measuredLeft: node.left,
    measuredTop: node.top,
    measuredWidth: node.width,
    nextRowId: node.nextRowId,
    numChildColumns: node.numChildColumns,
    parentId: node.parentId,
    previousRowId: node.previousRowId
  }, otherProps);
};
const buildHoverPositionForColumn = (node, otherProps) => {
  return Object.assign({
    depth: node.depth,
    firstChildRowId: node.firstChildRowId,
    hasLayoutStyles: node.hasLayoutStyles,
    id: node.id,
    isColumn: true,
    isModule: node.isModule,
    lastChildRowId: node.lastChildRowId,
    measuredHeight: node.height,
    measuredLeft: node.left,
    measuredTop: node.top,
    measuredWidth: node.width,
    nextColumnId: node.nextColumnId,
    numChildRows: node.numChildRows,
    parentId: node.parentId,
    parentRowHasRoomForMoreColumns: node.parentRowHasRoomForMoreColumns,
    previousColumnId: node.previousColumnId,
    unitsWide: node.unitsWide
  }, otherProps);
};

/**
 * Returns hover position stack for all layout trees given node positions and mouse coords
 * @param {Object} nodePositions Map of Arrays of all of the nodes (rows/columns/left item content)
 *   in each layout section, with top/bottom/left/right position, depth, id, and parent info for each
 * @param {Object} adjustedMouseCoords Currennt mouse x/y coordinates (adjusted for prev/current scroll bounds)
 * @return {Array} An array of nodes/inbetween areas that are hovered over (sorted deepest first)
 *   with the hovered edge/triangle included for each node
 */
const calculateLayoutTreeHoverPositions = (nodePositions, adjustedMouseCoords, {
  includeOutsideBuffer = false,
  allowNestedRows = true,
  outsideBufferOverride
}) => {
  const rootAreaIds = Object.keys(nodePositions);
  if (rootAreaIds.length === 0) {
    return [];
  }
  const hoveredStack = []; // stack of all the nodes hovered over, in ascending depth order
  const nodesToCheck = [];
  const rootLayoutTreePositions = [];
  let activeNodePositions = []; // Node positions for the layout section that is either hoveredOver, or closest to the mouse

  // Get the positions for the layout section that is hovered over, if there is one.
  // Also build the root layout section positions array
  rootAreaIds.forEach(key => {
    const nodePos = nodePositions[key][0];
    if (nodePos.depth === 0) {
      if (isPositionWithinBounds(adjustedMouseCoords, nodePos)) {
        activeNodePositions = nodePositions[key];
      }
      rootLayoutTreePositions.push(nodePos);
    } else {
      console.log('The first index in a node position array must be the root node');
    }
  });

  // If we are not hovering directly over any layout sections, determine hover
  // positions based off whichever layout section is the closest. If we are,
  // then add the first (depth 0) node position to the nodes to check
  let closestLayoutTreeId;
  let relativeLocation;
  if (!activeNodePositions.length) {
    const closestLayoutTreeData = getClosestDndArea(rootLayoutTreePositions, adjustedMouseCoords, outsideBufferOverride);
    closestLayoutTreeId = closestLayoutTreeData.closestLayoutTreeId;
    relativeLocation = closestLayoutTreeData.relativeLocation;
    if (closestLayoutTreeId && relativeLocation) {
      activeNodePositions = nodePositions[closestLayoutTreeId];
      // First push info for the closest layout section, which is now the root
      hoveredStack.push({
        id: closestLayoutTreeId,
        layoutSectionId: closestLayoutTreeId,
        layoutTree: closestLayoutTreeId,
        depth: 0,
        inBufferZone: true
      });
    }
  } else {
    nodesToCheck.push(activeNodePositions[0]);
  }
  const {
    mapOfPositionsById,
    mapOfChildrenById
  } = getLayoutTreeChildrenMaps(activeNodePositions);

  // Not hovering directly over a layout section. Figure out if we are within the outer buffer area to drop things onto.
  if (!nodesToCheck.length && includeOutsideBuffer) {
    const outerBufferHoverPositions = calculateOutsideLayoutTreeBufferAreaHoverPositions({
      adjustedMouseCoords,
      mapOfPositionsById,
      mapOfChildrenById,
      closestLayoutTreeId,
      relativeLocation,
      outsideBufferOverride
    });
    if (outerBufferHoverPositions) {
      hoveredStack.push(outerBufferHoverPositions);
    }
  }
  let previousRow;
  let previousColumn;
  let currentLayoutTreeId;
  const deepestHoveredNode = getDeepestHoveredNode(nodesToCheck, mapOfChildrenById, mapOfPositionsById, adjustedMouseCoords);
  while (nodesToCheck.length > 0) {
    const currentNodeToCheck = nodesToCheck.shift();
    const isLeaf = (mapOfChildrenById.get(currentNodeToCheck.id) || []).length === 0;
    if (currentNodeToCheck.depth === 0) {
      currentLayoutTreeId = currentNodeToCheck.id;
    }
    if (isPositionWithinBounds(adjustedMouseCoords, currentNodeToCheck)) {
      const hoverLocationProps = {
        topOrBottomHovered: determineTopOrBottomHalfPositionWithinBounds(adjustedMouseCoords, currentNodeToCheck),
        leftOrRightHovered: determineLeftOrRightHalfPositionWithinBounds(adjustedMouseCoords, currentNodeToCheck),
        triangleHovered: determinePositionTriangleWithinBounds(adjustedMouseCoords, currentNodeToCheck)
      };
      let resultForNode;
      if (currentNodeToCheck.isColumn) {
        resultForNode = buildHoverPositionForColumn(currentNodeToCheck, Object.assign({
          type: hoverTypes.INNER_TRIANGLE,
          layoutSectionId: currentLayoutTreeId,
          layoutTreeId: currentLayoutTreeId
        }, hoverLocationProps));
      } else if (currentNodeToCheck.isRow) {
        resultForNode = buildHoverPositionForRow(currentNodeToCheck, Object.assign({
          type: hoverTypes.INNER_TRIANGLE,
          layoutSectionId: currentLayoutTreeId,
          layoutTreeId: currentLayoutTreeId
        }, hoverLocationProps));
      }

      // Subtract one to account for the layout section node.
      const deepestRowAndColumnIndex = Math.floor((deepestHoveredNode.depth - 1) / 2);
      // Add 1 to begin index after the layout section.
      const currentRowAndColumnIndex = Math.floor((currentNodeToCheck.depth + 1) / 2);
      const isDeepestRowAndColumnIndex = currentRowAndColumnIndex >= deepestRowAndColumnIndex;
      const parentRow = mapOfPositionsById.get(deepestHoveredNode.parentId);
      const parentColumnOrSection = parentRow && mapOfPositionsById.get(parentRow.parentId);
      // More context on "special cases" to allow nested rows here: https://git.hubteam.com/HubSpot/ContentEditorUI/issues/8596
      allowNestedRows = allowNestedRows && parentColumnOrSection && parentColumnOrSection.depth >= 0 && parentColumnOrSection.numChildRows >= 2;
      let closeToEdgeDistances;
      if (allowNestedRows) {
        closeToEdgeDistances = bufferedCloseToEdgeDistancesWithNestedRows;
      } else {
        closeToEdgeDistances = bufferedCloseToEdgeDistancesWithoutNestedRows;
      }
      const {
        horizontalCloseToEdge,
        verticalCloseToEdge
      } = horizontalAndVericalCloseToEdgeDistanceForThisNode({
        // Since each buffer represents a certain percentage of width/height, we use the top-most (deepest) hovered
        // module as a reference for buffer sizes. See diagram in: https://git.hubteam.com/HubSpot/ContentEditorUI/pull/6988
        // for more context.
        width: deepestHoveredNode.width,
        height: deepestHoveredNode.height,
        isLeaf,
        closeToEdgeDistances,
        indexValues: {
          currentRowAndColumnIndex,
          isDeepestRowAndColumnIndex
        }
      });
      const customCloseToEdgeDistance = closeToEdgeDistancePerTriangle({
        triangleHovered: resultForNode.triangleHovered,
        horizontalCloseToEdge,
        verticalCloseToEdge
      });
      resultForNode.isCloseToTriangleEdge = isTrianglePositionCloseToEdgeOfBounds(customCloseToEdgeDistance, resultForNode.triangleHovered, adjustedMouseCoords, currentNodeToCheck);
      resultForNode.isCloseToTopOrBottomEdge = isCloseToTopOrBottomEdge(verticalCloseToEdge, resultForNode.topOrBottomHovered, adjustedMouseCoords, currentNodeToCheck);
      resultForNode.isCloseToLeftOrRightEdge = isCloseToLeftOrRightEdge(horizontalCloseToEdge, resultForNode.leftOrRightHovered, adjustedMouseCoords, currentNodeToCheck);

      // Add children to queue of nodes to check
      const children = getChildrenForCurrentNode(currentNodeToCheck.id, mapOfChildrenById, mapOfPositionsById);
      if (!children.length && currentNodeToCheck.id === currentLayoutTreeId) {
        resultForNode.isEmptyLayoutSection = true;
      }
      nodesToCheck.push(...children);

      // ==================
      // TODO: Prob move this into a helper function?
      // Check to see if this is close to child column
      // default this to false
      //
      resultForNode.isCloseToTopOfChildColumn = false;
      resultForNode.isCloseToBottomOfChildColumn = false;

      // Is this a non empty row?
      if (currentNodeToCheck.isRow && currentNodeToCheck.numChildColumns > 0) {
        // Figure out if any of the children are directly above or below the mouse
        const theChildColumnWeShareLateralSpaceWith = children.find(childColumn => {
          return isPositionWithinHorizontalBounds(adjustedMouseCoords, childColumn);
        });
        if (theChildColumnWeShareLateralSpaceWith) {
          // Figure out if we are above or below the child column
          if (adjustedMouseCoords.y < theChildColumnWeShareLateralSpaceWith.top) {
            // Are we closer to the top of this row, or the top of the child column?
            const distance = (theChildColumnWeShareLateralSpaceWith.top - currentNodeToCheck.top) * (VERTICAL_CHILD_COLUMN_BUFFER_PERCENTAGE / 100);
            const thePointOfNoReturn = theChildColumnWeShareLateralSpaceWith.top - distance;
            if (adjustedMouseCoords.y > thePointOfNoReturn) {
              resultForNode.isCloseToTopOfChildColumn = true;
            }
          } else if (adjustedMouseCoords.y > theChildColumnWeShareLateralSpaceWith.bottom) {
            // Are we closer to the bottom of this row, or the bottom of the child column?
            const distance = (currentNodeToCheck.bottom - theChildColumnWeShareLateralSpaceWith.bottom) * (VERTICAL_CHILD_COLUMN_BUFFER_PERCENTAGE / 100);
            const thePointOfNoReturn = theChildColumnWeShareLateralSpaceWith.bottom + distance;
            if (adjustedMouseCoords.y < thePointOfNoReturn) {
              resultForNode.isCloseToBottomOfChildColumn = true;
            }
          }

          // Okay, if we are close enough to the child column that we want to give the column the drop
          // zone, we'll pass along a "delegatedNode". We'll use the delegated node's meta data in the
          // reducer to figure out where to insert rows, instead of using this current (row) node.
          if (resultForNode.isCloseToTopOfChildColumn || resultForNode.isCloseToBottomOfChildColumn) {
            // Does this child column have children?
            const theColumnWeShareSpaceWithsChildren = getChildrenForCurrentNode(theChildColumnWeShareLateralSpaceWith.id, mapOfChildrenById, mapOfPositionsById);

            // If so, the node we'll want to insert rows around the grandchildren
            if (theColumnWeShareSpaceWithsChildren.length) {
              // If we are above the column, we want to insert a new row above the columns first child
              if (resultForNode.isCloseToTopOfChildColumn) {
                resultForNode.delegatedNodeToInsertAround = theColumnWeShareSpaceWithsChildren[0];
              } else {
                // Otherwise we should insert a new row after the columns last child
                resultForNode.delegatedNodeToInsertAround = theColumnWeShareSpaceWithsChildren[theColumnWeShareSpaceWithsChildren.length - 1];
              }
              // Otherwise no children, we want to insert a row around this "naked" column
            } else {
              resultForNode.delegatedNodeToInsertAround = theChildColumnWeShareLateralSpaceWith;
            }
          }
        }
      }
      // ================

      hoveredStack.push(resultForNode);
    } else if (
    // Check if position is in space between root area and first section
    includeOutsideBuffer && isFirstTopLevelSectionRow(currentNodeToCheck) && isPositionAboveBounds(adjustedMouseCoords, currentNodeToCheck)) {
      hoveredStack.push(buildHoverPositionForRow(currentNodeToCheck, {
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId,
        type: hoverTypes.IN_TOP_WHITESPACE
      }));
    } else if (
    // Check if position is in space between root area and last section
    includeOutsideBuffer && isLastTopLevelSectionRow(currentNodeToCheck) && isPositionBelowBounds(adjustedMouseCoords, currentNodeToCheck)) {
      hoveredStack.push(buildHoverPositionForRow(currentNodeToCheck, {
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId,
        type: hoverTypes.IN_BOTTOM_WHITESPACE
      }));
    } else if (
    // Check if position is in space between root area and the side of a section
    includeOutsideBuffer && isTopLevelSectionRow(currentNodeToCheck) && isPositionToTheLeftOfBounds(adjustedMouseCoords, currentNodeToCheck)) {
      const firstColumnNode = getFirstChildOfNode(currentNodeToCheck, mapOfChildrenById, mapOfPositionsById);
      hoveredStack.push(buildHoverPositionForColumn(firstColumnNode, {
        type: hoverTypes.IN_LEFT_WHITESPACE,
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId
      }));
    } else if (
    // Check if position is in space between root area and the side of a section
    includeOutsideBuffer && isTopLevelSectionRow(currentNodeToCheck) && isPositionToTheRightOfBounds(adjustedMouseCoords, currentNodeToCheck)) {
      const lastColumnNode = getLastChildOfNode(currentNodeToCheck, mapOfChildrenById, mapOfPositionsById);
      hoveredStack.push(buildHoverPositionForColumn(lastColumnNode, {
        type: hoverTypes.IN_RIGHT_WHITESPACE,
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId
      }));
    } else if (
    // Check if position is in space between two columns
    areColumnsInRow(previousColumn, currentNodeToCheck) && isBetweenTwoColumns(adjustedMouseCoords, currentNodeToCheck, previousColumn, mapOfPositionsById)) {
      hoveredStack.push({
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId,
        type: hoverTypes.BETWEEN_COLUMNS,
        leftId: previousColumn.id,
        rightId: currentNodeToCheck.id,
        parentId: previousColumn.parentId,
        parentRowHasRoomForMoreColumns: previousColumn.parentRowHasRoomForMoreColumns,
        measuredTop: previousColumn.top,
        measuredHeight: previousColumn.height,
        measuredLeftMidpoint: (previousColumn.right + currentNodeToCheck.left) / 2
      });
    } else if (
    // Check if position is in space between two rows
    areRowsInColumn(previousRow, currentNodeToCheck) && isBetweenTwoRows(adjustedMouseCoords, currentNodeToCheck, previousRow, mapOfPositionsById)) {
      hoveredStack.push({
        layoutSectionId: currentLayoutTreeId,
        layoutTreeId: currentLayoutTreeId,
        type: hoverTypes.BETWEEN_ROWS,
        topId: previousRow.id,
        bottomId: currentNodeToCheck.id,
        parentId: currentNodeToCheck.parentId,
        measuredLeft: previousColumn.left,
        measuredWidth: previousColumn.width,
        measuredTopMidpoint: (previousColumn.bottom + currentNodeToCheck.top) / 2
      });
    }
    if (currentNodeToCheck.isRow) {
      previousRow = currentNodeToCheck;
    } else {
      previousColumn = currentNodeToCheck;
    }
  }
  return hoveredStack.reverse();
};
export default calculateLayoutTreeHoverPositions;